/**
 * Centralized Logging Service
 *
 * This service provides a unified interface for logging across the application.
 *
 * Usage:
 * ```typescript
 * import { logger } from '@/lib/logger'
 *
 * // Error logging
 * logger.error('Failed to fetch user data', { userId: '123', error })
 *
 * // Warning logging
 * logger.warn('API response was slower than expected', { duration: 5000 })
 *
 * // Info logging (development only)
 * logger.info('User logged in successfully', { userId: '123' })
 *
 * // Debug logging (development only)
 * logger.debug('Processing checkout', { cartId: 'abc' })
 * ```
 */

type SeverityLevel = 'fatal' | 'error' | 'warning' | 'log' | 'info' | 'debug'

interface LogContext {
  [key: string]: unknown
}

interface LogOptions {
  /** Additional context to include with the log */
  context?: LogContext
  /** Tags for filtering */
  tags?: Record<string, string>
  /** User information */
  user?: {
    id?: string
    email?: string
    username?: string
  }
  /** Whether to send to external logger (currently disabled) */
  sendToSentry?: boolean
  /** Log level */
  level?: SeverityLevel
}

class Logger {
  private isDevelopment = process.env.NODE_ENV === 'development'
  private isLocal = process.env.NEXT_PUBLIC_ENV === 'local'

  /**
   * Log an error message
   */
  error(message: string, error?: Error | unknown, options?: LogOptions): void {
    const { context, tags, user } = options || {}

    // Always log to console in development
    if (this.isDevelopment || this.isLocal) {
      console.error(`[ERROR] ${message}`, {
        error,
        context,
        tags,
        user,
      })
    } else {
        console.error(`[ERROR] ${message}`, error)
    }
  }

  /**
   * Log a warning message
   */
  warn(message: string, options?: LogOptions): void {
    const { context, tags, user } = options || {}

    // Always log to console in development
    if (this.isDevelopment || this.isLocal) {
      console.warn(`[WARN] ${message}`, {
        context,
        tags,
        user,
      })
    } else {
        console.warn(`[WARN] ${message}`)
    }
  }

  /**
   * Log an info message
   * Only logs to console
   */
  info(message: string, options?: LogOptions): void {
    const { context } = options || {}

    // Only log in development/local
    if (this.isDevelopment || this.isLocal) {
      console.log(`[INFO] ${message}`, context || {})
    }
  }

  /**
   * Log a debug message
   * Only logs to console in development
   */
  debug(message: string, options?: LogOptions): void {
    const { context } = options || {}

    // Only log in development/local
    if (this.isDevelopment || this.isLocal) {
      console.log(`[DEBUG] ${message}`, context || {})
    }
  }

  /**
   * Set user context
   */
  setUser(user: { id?: string; email?: string; username?: string }): void {
    // No-op without external logger
  }

  /**
   * Set additional context
   */
  setContext(name: string, context: LogContext): void {
    // No-op without external logger
  }

  /**
   * Add breadcrumb
   */
  addBreadcrumb(
    message: string,
    data?: LogContext,
    level: SeverityLevel = 'info',
    tags?: Record<string, string>
  ): void {
    // No-op without external logger
  }

  /**
   * Capture exception manually
   */
  captureException(error: Error, options?: LogOptions): void {
    const { context, tags, user } = options || {}

    if (this.isDevelopment || this.isLocal) {
      console.error('[EXCEPTION]', error, {
        context,
        tags,
        user,
      })
    } else {
        console.error('[EXCEPTION]', error)
    }
  }

  /**
   * Capture message manually
   */
  captureMessage(
    message: string,
    level: SeverityLevel = 'info',
    options?: LogOptions
  ): void {
    const { context, tags, user } = options || {}

    if (this.isDevelopment || this.isLocal) {
      console.log(`[${level.toUpperCase()}] ${message}`, {
        context,
        tags,
        user,
      })
    } else {
        console.log(`[${level.toUpperCase()}] ${message}`)
    }
  }
}

// Export singleton instance
export const logger = new Logger()

// Export types for use in other files
export type { LogContext, LogOptions, SeverityLevel }
